import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchQuery } from '../utils/textSanitize';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  Target, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  CheckCircle,
  XCircle,
  Clock,
  Phone,
  Mail,
  Building2,
  User,
  FileText,
  AlertTriangle,
  Loader2,
  MessageCircle,
  BarChart3,
  TrendingUp,
  Calendar,
  X
} from 'lucide-react';
import NovaEmpresaModal from './NovaEmpresaModal';

const LeadsPage = () => {
  const { user, hasPermission } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    novos: 0,
    em_contato: 0,
    convertidos: 0,
    perdidos: 0,
    taxa_conversao: 0,
    leads_mes: 0
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadLeads();
    loadStats();
  }, [statusFilter, searchTerm]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError('');

      const listLeads = httpsCallable(functions, 'listLeads');
      const result = await listLeads({
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter === 'todos' ? undefined : statusFilter,
        search: searchTerm?.trim() ? sanitizeSearchQuery(searchTerm) : undefined
      });

      if (result.data?.success) {
        const data = result.data.data;
        setLeads(data.items || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          totalPages: data.pagination?.totalPages || 1
        }));
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar leads');
      }
    } catch (err) {
      console.error('Erro ao carregar leads:', err);
      setError('Erro ao carregar leads. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const getLeadsStats = httpsCallable(functions, 'getLeadsStats');
      const result = await getLeadsStats({});

      if (result.data?.success) {
        setStats(result.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const handleUpdateStatus = async (leadId, newStatus, motivoPerda = '') => {
    try {
      const updateLead = httpsCallable(functions, 'updateLead');
      const result = await updateLead({
        lead_id: leadId,
        status: newStatus,
        motivo_perda: motivoPerda || undefined
      });

      if (result.data?.success) {
        loadLeads();
        loadStats();
        setShowViewModal(false);
      } else {
        throw new Error(result.data?.message || 'Erro ao atualizar lead');
      }
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      alert('Erro ao atualizar status do lead');
    }
  };

  const handleAddNote = async () => {
    if (!notes.trim() || !selectedLead) return;

    try {
      const currentNotes = selectedLead.notas_contato || [];
      const newNotes = [
        ...currentNotes,
        {
          texto: notes,
          data: new Date().toISOString(),
          usuario: user.uid
        }
      ];

      const updateLead = httpsCallable(functions, 'updateLead');
      await updateLead({
        lead_id: selectedLead.id,
        notas_contato: newNotes
      });

      setNotes('');
      setShowNotesModal(false);
      loadLeads();
    } catch (err) {
      console.error('Erro ao adicionar nota:', err);
      alert('Erro ao adicionar nota');
    }
  };

  const handleConvertToCompany = async (lead) => {
    setSelectedLead(lead);
    setShowCompanyModal(true);
  };

  const handleCompanyCreated = async (result) => {
    if (!selectedLead) return;

    try {
      // O NovaEmpresaModal retorna um objeto com { company: { id: ... }, message: ... }
      const empresaId = result?.company?.id;
      
      if (!empresaId) {
        throw new Error('ID da empresa não encontrado no resultado');
      }

      const convertLeadToCompany = httpsCallable(functions, 'convertLeadToCompany');
      await convertLeadToCompany({
        lead_id: selectedLead.id,
        empresa_id: empresaId
      });

      setShowCompanyModal(false);
      setSelectedLead(null);
      loadLeads();
      loadStats();
      alert('Lead convertido em empresa com sucesso!');
    } catch (err) {
      console.error('Erro ao converter lead:', err);
      alert('Erro ao converter lead em empresa');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      novo: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Novo' },
      em_contato: { color: 'bg-yellow-100 text-yellow-800', icon: MessageCircle, label: 'Em Contato' },
      convertido: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Convertido' },
      perdido: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Perdido' }
    };

    const badge = badges[status] || badges.novo;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        <span>{badge.label}</span>
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    
    try {
      let d;
      
      // Se é um Timestamp do Firestore
      if (date && typeof date === 'object' && date.toDate) {
        d = date.toDate();
      } 
      // Se é um objeto com seconds (Timestamp serializado)
      else if (date && typeof date === 'object' && date.seconds) {
        d = new Date(date.seconds * 1000);
      }
      // Se é uma string ou número
      else {
        d = new Date(date);
      }
      
      // Verificar se é uma data válida
      if (isNaN(d.getTime())) {
        return '-';
      }
      
      return d.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error, date);
      return '-';
    }
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '-';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Target className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestão de Leads</h1>
              <p className="text-gray-600">Gerencie leads comerciais e converta em clientes</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Leads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Target className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Leads Novos</p>
                <p className="text-2xl font-bold text-blue-600">{stats.novos}</p>
              </div>
              <Clock className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Taxa de Conversão</p>
                <p className="text-2xl font-bold text-green-600">{stats.taxa_conversao}%</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Leads Este Mês</p>
                <p className="text-2xl font-bold text-purple-600">{stats.leads_mes}</p>
              </div>
              <Calendar className="h-10 w-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por empresa, CNPJ, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="todos">Todos os Status</option>
                <option value="novo">Novos</option>
                <option value="em_contato">Em Contato</option>
                <option value="convertido">Convertidos</option>
                <option value="perdido">Perdidos</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center space-x-3">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Leads Table */}
      {!loading && leads.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum lead encontrado</h3>
          <p className="text-gray-600">Aguarde novos leads chegarem pelo site público.</p>
        </div>
      )}

      {!loading && leads.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 text-gray-400 mr-2" />
                      <div>
                        <p className="font-medium text-gray-900">{lead.nome_empresa}</p>
                        <p className="text-sm text-gray-500">{formatCNPJ(lead.cnpj)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="font-medium text-gray-900 flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {lead.nome_contato}
                      </p>
                      <p className="text-gray-500 flex items-center mt-1">
                        <Mail className="h-4 w-4 mr-1" />
                        {lead.email}
                      </p>
                      <p className="text-gray-500 flex items-center mt-1">
                        <Phone className="h-4 w-4 mr-1" />
                        {lead.telefone}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(lead.status)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(lead.criado_em)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowViewModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="Ver detalhes"
                      >
                        <Eye className="h-5 w-5" />
                      </button>

                      {lead.status !== 'convertido' && lead.status !== 'perdido' && (
                        <>
                          <button
                            onClick={() => handleConvertToCompany(lead)}
                            className="text-green-600 hover:text-green-800"
                            title="Converter em empresa"
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>

                          <button
                            onClick={() => {
                              const motivo = prompt('Motivo da perda:');
                              if (motivo) {
                                handleUpdateStatus(lead.id, 'perdido', motivo);
                              }
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Marcar como perdido"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Lead Modal */}
      {showViewModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Detalhes do Lead</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Empresa</label>
                    <p className="text-gray-900 font-medium">{selectedLead.nome_empresa}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">CNPJ</label>
                    <p className="text-gray-900">{formatCNPJ(selectedLead.cnpj)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Contato</label>
                    <p className="text-gray-900">{selectedLead.nome_contato}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="text-gray-900">{selectedLead.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Telefone</label>
                    <p className="text-gray-900">{selectedLead.telefone}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <div>{getStatusBadge(selectedLead.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Data de Cadastro</label>
                    <p className="text-gray-900">{formatDate(selectedLead.criado_em)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Origem</label>
                    <p className="text-gray-900">{selectedLead.origem === 'site_publico' ? 'Site Público' : selectedLead.origem}</p>
                  </div>
                </div>

                {selectedLead.observacoes && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Observações</label>
                    <p className="text-gray-900 bg-gray-50 p-3 rounded-md">{selectedLead.observacoes}</p>
                  </div>
                )}

                {selectedLead.notas_contato && selectedLead.notas_contato.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">Histórico de Contatos</label>
                    <div className="space-y-2">
                      {selectedLead.notas_contato.map((nota, idx) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded-md">
                          <p className="text-sm text-gray-900">{nota.texto}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(nota.data).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-between mt-6 pt-4 border-t">
                <div className="space-x-2">
                  {selectedLead.status === 'novo' && (
                    <button
                      onClick={() => handleUpdateStatus(selectedLead.id, 'em_contato')}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 inline-flex items-center space-x-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      <span>Marcar Em Contato</span>
                    </button>
                  )}
                  
                  {(selectedLead.status === 'novo' || selectedLead.status === 'em_contato') && (
                    <>
                      <button
                        onClick={() => handleConvertToCompany(selectedLead)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 inline-flex items-center space-x-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>Converter em Empresa</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          const motivo = prompt('Motivo da não conversão (opcional):');
                          if (motivo !== null) { // Permite motivo vazio
                            handleUpdateStatus(selectedLead.id, 'perdido', motivo);
                          }
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 inline-flex items-center space-x-2"
                      >
                        <XCircle className="h-4 w-4" />
                        <span>Não Convertido</span>
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Modal */}
      {showCompanyModal && selectedLead && (
        <NovaEmpresaModal
          isOpen={showCompanyModal}
          onClose={() => {
            setShowCompanyModal(false);
            setSelectedLead(null);
          }}
          onSuccess={handleCompanyCreated}
          initialData={{
            nome_empresa: selectedLead.nome_empresa,
            cnpj: selectedLead.cnpj,
            email_empresa: selectedLead.email,
            telefone: selectedLead.telefone,
            admin_nome: selectedLead.nome_contato,
            admin_email: selectedLead.email
          }}
        />
      )}
    </div>
  );
};

export default LeadsPage;

